import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { COUNTRIES, INCOTERMS } from "../lib/constants";

/**
 * Structured full-address fields.
 * props:
 *  - values: object holding the address keys (supports a prefix, e.g. "ship_to_")
 *  - onChange: (field, value) => void  (field is the full key incl. prefix)
 *  - prefix: key prefix ("" for customer/vendor, "ship_to_" for documents)
 *  - disabled
 *  - testidPrefix: for data-testid namespacing
 */
export function AddressFields({ values = {}, onChange, prefix = "", disabled = false, testidPrefix = "addr" }) {
  const k = (f) => `${prefix}${f}`;
  const v = (f) => values[k(f)] || "";
  const set = (f) => (e) => onChange(k(f), e.target.value);
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="overline-label">Address line 1</Label>
        <Input value={v("address")} onChange={set("address")} disabled={disabled} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-address`} />
      </div>
      <div className="col-span-2">
        <Label className="overline-label">Address line 2</Label>
        <Input value={v("address2")} onChange={set("address2")} disabled={disabled} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-address2`} />
      </div>
      <div>
        <Label className="overline-label">City / Suburb</Label>
        <Input value={v("city")} onChange={set("city")} disabled={disabled} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-city`} />
      </div>
      <div>
        <Label className="overline-label">State / Province</Label>
        <Input value={v("state")} onChange={set("state")} disabled={disabled} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-state`} />
      </div>
      <div>
        <Label className="overline-label">Postcode</Label>
        <Input value={v("postcode")} onChange={set("postcode")} disabled={disabled} className="mt-1 rounded-sm" data-testid={`${testidPrefix}-postcode`} />
      </div>
      <div>
        <Label className="overline-label">Country</Label>
        <Select value={v("country") || undefined} onValueChange={(val) => onChange(k("country"), val)} disabled={disabled}>
          <SelectTrigger className="mt-1 rounded-sm" data-testid={`${testidPrefix}-country`}><SelectValue placeholder="Select country" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/**
 * Incoterms dropdown + free-text location.
 * props: incoterms, location, onChange(field, value), disabled, testidPrefix
 */
export function IncotermsFields({ incoterms = "", location = "", onChange, disabled = false, testidPrefix = "doc" }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="overline-label">Incoterms</Label>
        <Select value={incoterms || undefined} onValueChange={(val) => onChange("incoterms", val)} disabled={disabled}>
          <SelectTrigger className="mt-1 rounded-sm" data-testid={`${testidPrefix}-incoterms`}><SelectValue placeholder="Select Incoterms" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {INCOTERMS.map((i) => <SelectItem key={i.code} value={i.code}>{i.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="overline-label">Incoterms location</Label>
        <Input value={location} onChange={(e) => onChange("incoterms_location", e.target.value)} disabled={disabled} placeholder="e.g. Sydney, AU" className="mt-1 rounded-sm" data-testid={`${testidPrefix}-incoterms-location`} />
      </div>
    </div>
  );
}
